import { AuctionInfoWithItemIdInterface, ItemAttributeInterface } from '@on-arte/core-types';

import { ssrQueryKey } from '@onArte/shared/constants';
import { AuctionPublicDetails } from '@onArte/shared/interfaces';

import {
  ArtworkDimensions,
  buildMaterialLabel,
  buildTechniqueLabel,
  getArtworkDimensions,
  TranslateOrEmpty
} from './artworkMeta.utils';

/**
 * Dopisek odróżniający tytuły dzieł jednego artysty, które noszą tę samą nazwę (poz. 2).
 *
 * Szablon z umowy „[Nazwa dzieła] – [Imię artysty] | on*arte" daje dla nich ten sam wynik,
 * a umowa wymaga jednocześnie tytułu unikalnego na każdej stronie dzieła. Na produkcji
 * 17.09.2026 dotyczyło to 49 stron w 20 grupach. To NIE są aukcje jednego dzieła (te
 * załatwia mapa przekierowań i canonical), tylko osobne dzieła o tej samej nazwie.
 *
 * Tę samą funkcję wołają serwer i przeglądarka, na tych samych danych z API — dlatego
 * tytuł z serwera i tytuł ustawiony po podłączeniu aplikacji się zgadzają.
 */

/**
 * Tyle aukcji artysty pobieramy, żeby znaleźć dzieła o tej samej nazwie.
 *
 * Musi być identyczne po obu stronach, inaczej grupy mogłyby się różnić.
 * ponytail: najwięcej czynnych aukcji jednego artysty to 46 (17.09.2026); przy ponad
 * 100 część grupy wypadnie poza pierwszą stronę listy i trzeba będzie stronicować.
 */
export const artistWorksLimit: number = 100;

/**
 * Klucz zasiewu z dopiskiem, którego serwer użył w tytule strony wejścia.
 *
 * Przeglądarka ma wyłączone ponawianie zapytań, więc jedno nieudane pobranie po jej stronie
 * skasowałoby poprawny dopisek z tytułu na całą wizytę. Z tym zasiewem przeglądarka zostaje wtedy
 * przy tytule z serwera, a przy pierwszym renderowaniu ustawia go od razu, bez czekania na zapytania.
 */
export function artworkTitleSuffixQueryKey(auctionId: string): [string, string] {
  return ssrQueryKey(`artworkTitleSuffix-${auctionId}`);
}

export interface ArtworkDistinctiveFeatures {
  year: string | null;
  dimensions: string | null;
  technique: string | null;
  surface: string | null;
}

export interface ArtworkNamesake {
  id: string;
  features: ArtworkDistinctiveFeatures;
}

/**
 * Kolejność ma znaczenie: dopisek bierze pierwszy zestaw cech, który rozróżnia całą grupę.
 * Rok i wymiary są krótkie i zawsze czyste, technika i podłoże bywają listami, więc idą dalej.
 */
const featureOrder: (keyof ArtworkDistinctiveFeatures)[] = ['year', 'dimensions', 'technique', 'surface'];

/** Wszystkie zestawy o danej liczebności, w kolejności wejścia (jak `itertools.combinations`). */
function combinations<T>(items: T[], size: number, start: number = 0): T[][] {
  if (size === 0) {
    return [[]];
  }

  const result: T[][] = [];
  for (let index: number = start; index <= items.length - size; index++) {
    combinations(items, size - 1, index + 1)
      .forEach((rest: T[]): void => { result.push([items[index], ...rest]); });
  }

  return result;
}

const featureCombinations: (keyof ArtworkDistinctiveFeatures)[][] = [];
for (let size: number = 1; size <= featureOrder.length; size++) {
  featureCombinations.push(...combinations(featureOrder, size));
}

function formatCentimeters(value: number): string {
  return String(value).replace('.', ',');
}

function formatDimensions(dimensions: ArtworkDimensions): string | null {
  return dimensions.width && dimensions.height
    ? `${formatCentimeters(dimensions.width)} × ${formatCentimeters(dimensions.height)} cm`
    : null;
}

export function buildArtworkDistinctiveFeatures(
  details: AuctionPublicDetails,
  translate: TranslateOrEmpty
): ArtworkDistinctiveFeatures {
  const attributes: ItemAttributeInterface[] = details.itemDetails.attributes ?? [];

  return {
    year: details.itemDetails.year?.trim() || null,
    dimensions: formatDimensions(getArtworkDimensions(attributes)),
    technique: buildTechniqueLabel(attributes, translate)?.toLocaleLowerCase('pl') ?? null,
    surface: buildMaterialLabel(attributes, translate)?.toLocaleLowerCase('pl') ?? null,
  };
}

/**
 * Identyfikatory aukcji reprezentujących dzieła artysty o tej samej nazwie, od najstarszego.
 *
 * Członkiem grupy jest DZIEŁO, nie aukcja. Na produkcji zdarzają się dwie czynne aukcje
 * jednego dzieła (np. „FRAGILITY 2.0", utworzone 4 sekundy po sobie) — to przypadek z poz. 11,
 * który rozwiązuje canonical, a nie dwa dzieła do rozróżnienia numerem. Bieżące dzieło zawsze
 * reprezentuje bieżąca aukcja, pozostałe — ich najstarsza aukcja.
 *
 * Core oddaje listę od najnowszej (`createdAtDatetime` malejąco), więc ją odwracamy: numer 1
 * dostaje dzieło wystawione najwcześniej. Gdy bieżącej aukcji nie ma wśród czynnych
 * (np. zakończona), nie wiadomo, gdzie ją postawić, więc tytuł zostaje bez dopisku.
 */
export function findNamesakeAuctionIds(
  current: AuctionPublicDetails,
  artistAuctions: AuctionInfoWithItemIdInterface[]
): string[] {
  const oldestFirst: AuctionInfoWithItemIdInterface[] = artistAuctions
    .filter((auction: AuctionInfoWithItemIdInterface): boolean => auction.name === current.itemDetails.name)
    .reverse();
  if (!oldestFirst.some((auction: AuctionInfoWithItemIdInterface): boolean => auction.id === current.id)) {
    return [];
  }

  // Map zachowuje kolejność pierwszego wstawienia, także po nadpisaniu wartości klucza.
  const auctionIdByItem: Map<string, string> = new Map();
  oldestFirst.forEach((auction: AuctionInfoWithItemIdInterface): void => {
    if (!auctionIdByItem.has(auction.itemId)) {
      auctionIdByItem.set(auction.itemId, auction.id);
    }
  });
  auctionIdByItem.set(current.itemDetails.id, current.id);

  return Array.from(auctionIdByItem.values());
}

function formatSuffix(member: ArtworkNamesake, combination: (keyof ArtworkDistinctiveFeatures)[]): string {
  return combination
    .map((feature: keyof ArtworkDistinctiveFeatures): string | null => member.features[feature])
    .filter((value: string | null): value is string => !!value)
    .join(', ');
}

/**
 * Dopisek bieżącego dzieła albo `null`, gdy tytuł i tak jest unikalny.
 *
 * `group` to dzieła grupy od najstarszego, razem z bieżącym. Bierzemy najmniejszy zestaw cech,
 * który rozróżnia całą grupę, i oddajemy dopisek bieżącego dzieła. Gdy dane nie rozróżniają
 * niczego, dopiskiem jest numer w kolejności wystawienia (decyzja Przemka z 18.09.2026).
 * Po uzupełnieniu danych przez klienta numer znika sam, bo pierwszeństwo mają prawdziwe cechy.
 *
 * Unikalność sprawdzamy na GOTOWYCH dopiskach, nie na surowych cechach: różne zestawy wartości
 * potrafią dać ten sam tekst po pominięciu pustych pól — technika „tkanina" bez podłoża i podłoże
 * „tkanina" bez techniki to dwa razy „(tkanina)". Jeden pusty dopisek w grupie jest dopuszczalny:
 * to dzieło zostaje przy samym szablonie, a pozostałe i tak się od niego różnią.
 */
export function buildArtworkTitleSuffix(group: ArtworkNamesake[], currentId: string): string | null {
  const currentIndex: number = group.findIndex((member: ArtworkNamesake): boolean => member.id === currentId);
  if (group.length < 2 || currentIndex < 0) {
    return null;
  }

  for (const combination of featureCombinations) {
    const suffixes: string[] = group.map((member: ArtworkNamesake): string => formatSuffix(member, combination));
    if (new Set(suffixes).size === group.length) {
      return suffixes[currentIndex] || null;
    }
  }

  return String(currentIndex + 1);
}
